import { useState } from "react";
import "./module-picture.css";

interface ModulePictureProps {
  /** Slug bajo `public/screenshots/<slug>.{avif,webp,png}`. Convención: el
   *  module key (`playground`, `cafe`, …). HeroCube usa slugs distintos
   *  porque cada cara es una pantalla concreta, no el módulo entero. */
  slug: string;
  /** Pretitulo pequeño del placeholder (ej. "MÓDULO"). */
  eyebrow: string;
  /** Título visible del placeholder (ej. "Parque infantil"). */
  label: string;
  /** Si la imagen va con `loading="eager"` + `fetchpriority="high"`. Sólo
   *  las 2 primeras de la galería del hub. El resto en `lazy`. */
  eager?: boolean;
  /** Clase extra para el wrapper. */
  className?: string;
}

// Picture compartida por la galería del hub (`ModulesGallery`) y la spoke
// `/features`. Reutiliza el mismo patrón que `HeroCube.tsx`: placeholder
// gradient navy→teal con eyebrow + label SIEMPRE renderizado, <picture>
// AVIF/WebP/PNG superpuesta con `onError` para colapsar silenciosamente
// hasta que David suba los archivos a `public/screenshots/`.
//
// TODO: replace placeholder with real capture — public/screenshots/<slug>.{avif,webp,png}
export default function ModulePicture({
  slug,
  eyebrow,
  label,
  eager = false,
  className = "",
}: ModulePictureProps) {
  const [hasImage, setHasImage] = useState(true);

  return (
    <div className={`module-picture ${className}`} aria-hidden="true">
      <div className="module-picture-placeholder">
        <span className="module-picture-eyebrow">{eyebrow}</span>
        <span className="module-picture-label">{label}</span>
      </div>

      {hasImage && (
        <picture>
          <source srcSet={`/screenshots/${slug}.avif`} type="image/avif" />
          <source srcSet={`/screenshots/${slug}.webp`} type="image/webp" />
          <img
            src={`/screenshots/${slug}.png`}
            alt=""
            className="module-picture-image"
            loading={eager ? "eager" : "lazy"}
            // Mismo motivo que en HeroCube: pasamos `fetchpriority` lowercase
            // vía spread + cast para evitar el warning de React 18.3.
            {...({ fetchpriority: eager ? "high" : "auto" } as Record<string, string>)}
            decoding={eager ? "sync" : "async"}
            onError={() => setHasImage(false)}
          />
        </picture>
      )}
    </div>
  );
}
